import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Carousel, CarouselContent, CarouselItem, CarouselApi } from "@/components/ui/carousel";
import { ArrowLeft, Heart, HeartHandshake, Eye, Star, Calendar, MapPin, Camera, Flame, Crown, Coins, Lock, Unlock, MessageCircle, Volume2, FileImage, Plus, Zap, X, CircleDollarSign, Dumbbell, ChefHat, Plane, Music, Film, BookOpen, Palette, Laptop, Gamepad2, Dog, Trophy, Sparkles, Tag, ChevronRight, Trash2, RotateCcw, Play, Flag } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { apiClient } from "@/api/ApiClient";
import { ReportImageDialog } from "@/components/ReportImageDialog";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";


import sampleCoupleHug from "@/assets/sample-couple-hug.jpg";
import adamPhoto1 from "@/assets/adam-photo-1.jpg";

// No avatar maps or defaults

const INTEREST_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  fitness: Dumbbell,
  cooking: ChefHat,
  travel: Plane,
  music: Music,
  movies: Film,
  books: BookOpen,
  art: Palette,
  tech: Laptop,
  technology: Laptop,
  gaming: Gamepad2,
  games: Gamepad2,
  pets: Dog,
  animals: Dog,
  sports: Trophy,
  // CS labels
  knihy: BookOpen,
  hudba: Music,
  filmy: Film,
  cestování: Plane,
  cestovani: Plane,
  vaření: ChefHat,
  vareni: ChefHat,
  umění: Palette,
  umeni: Palette,
  technologie: Laptop,
  hry: Gamepad2,
  sport: Trophy,
  zvířata: Dog,
  zvirata: Dog,
};

const getInterestIcon = (interest: string): React.ComponentType<{ className?: string }> => {
  const key = interest.toLowerCase().replace(/^boyfriend_attr\.interests\./, '').trim();
  return INTEREST_ICONS[key] || Sparkles;
};

const ZODIAC_SYMBOLS: Record<ZodiacKey, string> = {
  aries: '♈',
  taurus: '♉',
  gemini: '♊',
  cancer: '♋',
  leo: '♌',
  virgo: '♍',
  libra: '♎',
  scorpio: '♏',
  sagittarius: '♐',
  capricorn: '♑',
  aquarius: '♒',
  pisces: '♓',
};

const getZodiacSymbol = (key: ZodiacKey | undefined | null): string => {
  if (!key) return '✨';
  return ZODIAC_SYMBOLS[key] ?? '✨';
};

interface ProfilePageProps {
  boyfriendId: string;
  user: {
    id?: string;
    email?: string;
    name?: string;
    isPremium?: boolean;
  } | null;
  onBack: () => void;
  onPromptLogin: () => void;
  onNavigateToChat: () => void;
  onNavigateToMyBoyfriends: () => void;
  onNavigateToProfile: () => void;
  onNavigateToSettings: () => void;
  onNavigateToCharacterCreation: () => void;
  onNavigateToExplore: () => void;
  onNavigateToPremium: () => void;
  userTokens?: number;
  onUpdateTokens?: (newTokens: number) => void;
  onPurchaseTokens?: (tokens: number, paymentMethod: string) => void;
  onTokensClick?: () => void;
  source?: 'chat' | 'explore' | 'myBoyfriends';
  favorites?: Set<string>;
  onToggleFavorite?: (boyfriendId: string) => void;
}

// Import BoyfriendProfile from API models
import { BoyfriendProfile, ZodiacKey } from '@/api/models';

export const ProfilePage = ({ 
  boyfriendId, 
  user, 
  onBack, 
  onPromptLogin,
  onNavigateToChat,
  onNavigateToMyBoyfriends,
  onNavigateToProfile,
  onNavigateToSettings,
  onNavigateToCharacterCreation,
  onNavigateToExplore,
  onNavigateToPremium,
  userTokens = 0,
  onUpdateTokens,
  onPurchaseTokens,
  onTokensClick,
  source = 'chat',
  favorites = new Set(),
  onToggleFavorite
}: ProfilePageProps) => {
  const [profile, setProfile] = useState<BoyfriendProfile | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  // Token/purchase flow is centralized in TokenBalanceModal; open it via onTokensClick
  const [generationType, setGenerationType] = useState<'photo'>('photo');
  const [generatePrompt, setGeneratePrompt] = useState('');
  const [unlockedPhotos, setUnlockedPhotos] = useState<Set<string>>(new Set());
  const [spicyPhotos, setSpicyPhotos] = useState<Array<{ url: string; unlockKey: string; locked: boolean; isLockable: boolean; priceTokens: number | null; description: string | null }>>([]);
  const [fullScreenPhoto, setFullScreenPhoto] = useState<string | null>(null);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  // Local state for tokens in modal - always loaded from API, updates immediately after unlock
  const [modalTokens, setModalTokens] = useState<number>(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPlayingVoice, setIsPlayingVoice] = useState(false);
  const [referenceImageUrl, setReferenceImageUrl] = useState<string>('');
  const [uploadedUserPhoto, setUploadedUserPhoto] = useState<string>('');
  const [showGenerationDialog, setShowGenerationDialog] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  const [generatedImageId, setGeneratedImageId] = useState<string | null>(null);
  const [reportImageId, setReportImageId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [isDeletingChat, setIsDeletingChat] = useState(false);
  const [isResettingChat, setIsResettingChat] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  // Removed hardcoded sample profiles

  // Reference image can default to current profile's avatar when loaded

  useEffect(() => {
    const loadProfile = async () => {
      setIsLoadingProfile(true);
      
      // Try API first by boyfriend ID
      try {
        const data = await apiClient.get(`/boyfriends/${boyfriendId}`);
        if (data) {
          if (data.avatarUrl) {
            setReferenceImageUrl(data.avatarUrl);
          }
          // Normalize interests
          let interests: string[] = [];
          if (Array.isArray(data.interests)) {
            interests = data.interests;
          } else if (typeof data.interests === 'string') {
            try {
              const parsed = JSON.parse(data.interests);
              interests = Array.isArray(parsed) ? parsed : [data.interests];
            } catch {
              interests = [data.interests];
            }
          }
          const parsedAge = typeof data.age === 'number' ? data.age : (typeof data.age === 'string' ? parseInt(data.age) : undefined);

          const apiProfile: BoyfriendProfile = {
            id: data.id ?? boyfriendId,
            name: data.name,
            avatarUrl: data.avatarUrl,
            personality: data.personality,
            age: typeof parsedAge === 'number' && !isNaN(parsedAge) ? parsedAge.toString() : undefined,
            ethnicity: data.ethnicity || '',
            bodyType: data.bodyType || '',
            hairstyle: data.hairstyle || '',
            clothingStyle: data.clothingStyle || '',
            interests: interests,
            tattoos: data.tattoos || '',
            communicationStyle: data.communicationStyle || '',
            isPremium: data.isPremium || false,
            location: data.location,
            occupation: data.occupation,
            bio: data.bio,
            rating: data.rating || 0,
            popularity: data.popularity || 0,
            style: data.style || (data as any).art_style || undefined,
            createdAt: data.createdAt || new Date().toISOString(),
            photosUrl: Array.isArray(data.photosUrl) ? data.photosUrl : [],
            spicyPhotosUrl: Array.isArray(data.spicyPhotosUrl) ? data.spicyPhotosUrl : [],
            stats: typeof data.stats === 'object' ? data.stats : undefined,
            zodiac: data.zodiac,
            zodiacKey: data.zodiacKey,
          } as BoyfriendProfile;

          setProfile(apiProfile);
          setIsLoadingProfile(false);
          return;
        }
      } catch (error) {
        console.error('Error loading boyfriend via API:', error);
      }


      setIsLoadingProfile(false);
    };
    
    loadProfile();
  }, [boyfriendId, user?.id]);

  // Carousel effect to track current slide
  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    setCount(carouselApi.scrollSnapList().length);
    setCurrent(carouselApi.selectedScrollSnap() + 1);

    carouselApi.on("select", () => {
      setCurrent(carouselApi.selectedScrollSnap() + 1);
    });
  }, [carouselApi]);

  // Function to scroll to specific slide
  const scrollTo = useCallback((index: number) => {
    carouselApi?.scrollTo(index);
  }, [carouselApi]);


  // Load tokens from API when any modal opens - always fetch fresh tokens to ensure accuracy
  useEffect(() => {
    if (showGenerateModal && user?.id) {
      const loadTokens = async () => {
        try {
          // Always fetch fresh tokens from API when modal opens to ensure accuracy
          // Use /users/me endpoint which returns User object with currentTokens
          const userData = await apiClient.get('/users/me');
          const freshTokens = userData?.currentTokens || 0;
          setModalTokens(freshTokens);
          // Also update parent if callback exists
          if (onUpdateTokens) {
            onUpdateTokens(freshTokens);
          }
        } catch (error) {
          console.error('Error fetching user tokens:', error);
          setModalTokens(0);
        }
      };
      loadTokens();
    } else if (showGenerateModal) {
      // No user logged in
      setModalTokens(0);
    }
  }, [showGenerateModal, user?.id]);

  // Load spicy photos with unlock status
  useEffect(() => {
    const loadSpicyPhotos = async () => {
      if (!profile || !profile.id) {
        return;
      }
      
      try {
        const photos = await apiClient.getSpicyPhotos(profile.id);
        if (Array.isArray(photos)) {
          setSpicyPhotos(photos);
        }
      } catch (error) {
        console.error('Error loading spicy photos:', error);
        toast({
          title: t('boyfriend_profile.load_error') || 'Failed to load spicy photos',
          variant: 'destructive',
        });
        // Fallback to empty array
        setSpicyPhotos([]);
      }
    };
    
    loadSpicyPhotos();
  }, [boyfriendId, profile?.id]); // Removed toast and t from dependencies to prevent infinite loop

  const handleToggleFavorite = async () => {
    if (!profile || !onToggleFavorite) {
      console.log('Missing profile or onToggleFavorite function');
      return;
    }
    console.log('Toggling favorite for profile:', profile.id, profile.name);
    await onToggleFavorite(profile.id);
  };

  const handleUnlockSpicyPhoto = async (unlockKey: string, priceTokens: number | null) => {
    if (!user || !profile) {
      onPromptLogin();
      return;
    }

    if (!user.isPremium) {
      onNavigateToPremium();
      return;
    }

    if (priceTokens == null || priceTokens <= 0) {
      toast({
        title: t('boyfriend_profile.unlock_error') || 'Invalid price for this photo',
        variant: 'destructive',
      });
      return;
    }

    const requiredTokens = priceTokens;
    if (modalTokens < requiredTokens) {
      onTokensClick?.();
      return;
    }
    
    try {
      const result = await apiClient.unlockImage(unlockKey);
      
      if (result.success) {
        // Reload spicy photos to get updated unlock status
        const photos = await apiClient.getSpicyPhotos(profile.id);
        if (Array.isArray(photos)) {
          setSpicyPhotos(photos);
        }
        
        // Update tokens from backend response (backend returns currentTokens after deduction)
        if (result.currentTokens !== undefined) {
          // Update modal tokens immediately for UI refresh
          setModalTokens(result.currentTokens);
          // Also update parent component
          if (onUpdateTokens) {
            onUpdateTokens(result.currentTokens);
          }
        }
        
        toast({
          title: t('boyfriend_profile.unlock_success'),
          description: t('boyfriend_profile.unlock_success_desc'),
          variant: 'default',
        });
      } else {
        toast({
          title: result.error || (t('boyfriend_profile.unlock_error') || 'Failed to unlock photo'),
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error unlocking photo:', error);
      toast({
        title: error?.message || (t('boyfriend_profile.unlock_error') || 'Failed to unlock photo'),
        variant: 'destructive',
      });
    }
  };

  const handleGenerate = async () => {
    if (!user) {
      onPromptLogin();
      return;
    }
    
    const cost = 0; // Set to 0 for testing
    if (modalTokens < cost) {
      try {
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('resumeGenerationAfterTokens', '1');
        }
      } catch {}
      onTokensClick?.();
      return;
    }

    if (!generatePrompt.trim()) {
      toast({
        title: "Prompt Required",
        description: "Please describe your vision before generating.",
        variant: "destructive"
      });
      return;
    }
    
    // Open generation dialog and start generating
    setShowGenerationDialog(true);
    setGeneratedImageUrl(null);
    setIsGenerating(true);
    setShowGenerateModal(false); // Close the prompt modal
    
    try {
      const res = await apiClient.generateImage({ userPrompt: generatePrompt, boyfriendId, userPhotoBase64: uploadedUserPhoto });
      const imageUrl = res?.imageUrl || null;
      if (!imageUrl) {
        toast({
          title: "No image returned",
          description: "The server did not return an image URL.",
          variant: "destructive"
        });
        setIsGenerating(false);
        setShowGenerationDialog(false);
        return;
      }
      setGeneratedImageUrl(imageUrl);
      setGeneratedImageId(res?.imageId || null);
      setShowGenerationDialog(true);
      // Token balance is updated on server; optionally refresh elsewhere
      setIsGenerating(false);
      setGeneratePrompt('');
      toast({ title: "Image Generated!", description: "Your custom image has been created." });
    } catch (error: any) {
      if (error?.status === 400) {
        setIsGenerating(false);
        setShowGenerationDialog(false);
        try {
          if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem('resumeGenerationAfterTokens', '1');
          }
        } catch {}
        onTokensClick?.();
        return;
      }
      console.error('Error generating image:', error);
      toast({
        title: "Generation Failed",
        description: error?.message || (error instanceof Error ? error.message : "An unexpected error occurred."),
        variant: "destructive"
      });
      setIsGenerating(false);
      setShowGenerationDialog(false);
    }
  };

  // When token modal closes and we need to resume generation, reopen the generate modal
  useEffect(() => {
    const handler = () => {
      try {
        const flag = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('resumeGenerationAfterTokens') === '1';
        if (flag) {
          sessionStorage.removeItem('resumeGenerationAfterTokens');
          setShowGenerateModal(true);
        }
      } catch {}
    };
    window.addEventListener('resume-generation', handler);
    return () => window.removeEventListener('resume-generation', handler);
  }, []);

  const handleDeleteChat = async () => {
    if (!profile) return;
    setIsDeletingChat(true);
    try {
      await apiClient.delete(`/chats/${profile.id}`);
      toast({ title: t('boyfriend_profile.chat_deleted') });
      setShowDeleteConfirm(false);
      onBack();
    } catch (error: any) {
      console.error('Error deleting chat:', error);
      toast({
        title: error?.message || t('boyfriend_profile.action_failed'),
        variant: 'destructive',
      });
    } finally {
      setIsDeletingChat(false);
    }
  };

  const handleResetConversation = async () => {
    if (!profile) return;
    setIsResettingChat(true);
    try {
      await apiClient.delete(`/chats/${profile.id}/messages`);
      toast({ title: t('boyfriend_profile.conversation_reset') });
      setShowResetConfirm(false);
    } catch (error: any) {
      console.error('Error resetting conversation:', error);
      toast({
        title: error?.message || t('boyfriend_profile.action_failed'),
        variant: 'destructive',
      });
    } finally {
      setIsResettingChat(false);
    }
  };

  const handlePlayVoice = async () => {
    if (!profile?.id) return;
    try {
      setIsPlayingVoice(true);
      const sampleName =
        (profile.name || "").trim() || t("boyfriend_profile.voice_sample_name_fallback");
      const blob = await apiClient.generateBoyfriendVoice({
        boyfriendId: profile.id,
        text: t("boyfriend_profile.voice_sample_tts", { name: sampleName }),
      });
      const audioUrl = URL.createObjectURL(blob);
      const audio = new Audio(audioUrl);
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        setIsPlayingVoice(false);
      };
      audio.onerror = () => {
        URL.revokeObjectURL(audioUrl);
        setIsPlayingVoice(false);
      };
      await audio.play();
    } catch (error: any) {
      setIsPlayingVoice(false);
      toast({
        title: error?.message || "Voice playback failed",
        variant: "destructive",
      });
    }
  };

  // Show loading state - Skeleton profile
  if (isLoadingProfile) {
    return (
      <div className="bg-gradient-dreamy min-h-screen pb-16 overflow-x-hidden" style={{ paddingTop: "var(--safe-area-inset-top, 0px)" }}>
        {/* Skeleton Photo */}
        <Skeleton className="w-full aspect-square rounded-none" />
        {/* Skeleton Content */}
        <div className="p-6 space-y-6">
          {/* Action Buttons */}
          <div className="flex gap-3">
            <Skeleton className="h-10 w-28 rounded-md" />
            <Skeleton className="h-10 w-36 rounded-md" />
          </div>
          <div className="flex gap-3">
            <Skeleton className="h-10 w-36 rounded-md" />
            <Skeleton className="h-10 w-28 rounded-md" />
          </div>
          {/* About */}
          <div className="space-y-3">
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
          {/* Personality */}
          <div className="space-y-3">
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-7 w-24 rounded-full" />
          </div>
          {/* Interests */}
          <div className="space-y-3">
            <Skeleton className="h-5 w-16" />
            <div className="flex flex-wrap gap-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-6 w-16 rounded-full" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show profile not found only after loading is complete
  if (!profile) {
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-dreamy">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">{t('boyfriend_profile.not_found')}</h2>
          <Button onClick={onBack} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('boyfriend_profile.go_back')}
          </Button>
        </div>
      </div>
    );
  }



  return (
    <div className="bg-gradient-dreamy min-h-screen pb-16 overflow-x-hidden">
      {/* Main Content */}
      <div className="overflow-y-auto overflow-x-hidden" style={{ paddingTop: "var(--safe-area-inset-top, 0px)" }}>
        {/* Profile Header Carousel - Full Width */}
        <div className="relative">
          {/* Floating Back Button */}
          <button
            type="button"
            onClick={onBack}
            aria-label={source === 'explore' ? t('boyfriend_profile.back_to_explore') : source === 'myBoyfriends' ? t('boyfriend_profile.back_to_my_boyfriends') : t('boyfriend_profile.back_to_chat')}
            className="absolute top-3 left-6 z-20 h-10 w-10 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/55 backdrop-blur-md border border-white/20 text-white shadow-lg transition-all"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <Carousel className="w-full" setApi={setCarouselApi}>
            <CarouselContent>
              {(() => {
                const rest = (profile.photosUrl || []).filter((p) => p && p !== profile.avatarUrl);
                const all = profile.avatarUrl ? [profile.avatarUrl, ...rest] : rest;
                return all;
              })().map((photo, index) => (
                <CarouselItem key={index}>
                  <div className="relative aspect-square bg-gradient-to-br from-accent/50 to-background">
                    <img
                      src={photo}
                      alt={`${profile.name} photo ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          
          {/* Premium Badge - Top Left */}
          {profile.isPremium && (
            <div className="absolute top-3 left-6 z-10">
              <div className="bg-amber-500/20 backdrop-blur-sm border border-amber-400/40 rounded-full px-3 py-1.5 flex items-center gap-1.5">
                <Crown className="h-4 w-4 text-amber-300" />
                <span className="text-xs font-medium text-amber-100">Premium</span>
              </div>
            </div>
          )}

          {/* Favorites Button - Floating Top Right */}
          <div className="absolute top-3 right-6 z-10">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleToggleFavorite}
              className={`h-10 w-10 rounded-full bg-black/40 hover:bg-black/55 backdrop-blur-md border border-white/20 shadow-lg transition-all duration-300 hover:scale-110 ${
                favorites.has(profile.id)
                  ? 'text-red-500 hover:text-red-500'
                  : 'text-white hover:text-red-500'
              }`}
            >
              <Heart 
                className={`h-5 w-5 transition-all duration-300 ${
                  favorites.has(profile.id) 
                    ? 'fill-current scale-110 drop-shadow-lg' 
                    : 'fill-none hover:fill-current/30'
                }`}
              />
            </Button>
          </div>
          
          {/* Dots Indicator */}
          {count > 1 && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
              {Array.from({ length: count }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => scrollTo(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-200 ${
                    current === index + 1
                      ? 'bg-white scale-125'
                      : 'bg-white/50 hover:bg-white/75'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Chat Button - Floating Bottom Right */}
          <div className="absolute bottom-4 right-4 z-20">
            <Button
              variant="romantic"
              onClick={onNavigateToChat}
              className="hover:bg-gradient-to-r hover:from-pink-500 hover:to-purple-500 transition-all duration-300 hover:scale-125 hover:shadow-lg hover:shadow-pink-500/50"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              {t('boyfriend_profile.chat_now')}
            </Button>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="max-w-6xl p-6">

        {/* Profile Info */}
        <div className="space-y-6 mb-8 text-left">
            {/* Name / Age / Occupation / Bio Header */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold leading-tight">
                  {profile.name}{profile.age ? ',' : ''} <span className="text-2xl font-normal text-muted-foreground">{profile.age}</span>
                </h1>
                <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
              </div>
              {profile.occupation && (
                <p className="text-base font-medium text-primary">{profile.occupation}</p>
              )}
              {profile.bio && (
                <p className="text-sm text-foreground/90 leading-relaxed pt-1">{profile.bio}</p>
              )}
            </div>

            {(profile.personality || profile.zodiac) && (
              <div className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('boyfriend_profile.personality')}</h3>
                <div className="flex flex-wrap items-center gap-2">
                  {profile.zodiac && (
                    <span className="inline-flex items-center gap-1.5 bg-secondary/60 rounded-full px-3 py-1.5 text-sm capitalize">
                      <span aria-hidden="true" className="text-base leading-none">{getZodiacSymbol(profile.zodiacKey)}</span>
                      {profile.zodiac}
                    </span>
                  )}
                  {profile.personality && (
                    <span className="bg-secondary/60 rounded-full px-3 py-1.5 text-sm capitalize">
                      {t(profile.personality)}
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('boyfriend_profile.interests')}</h3>
              <div className="flex flex-wrap gap-2">
                {(profile.interests || []).map((interest, index) => {
                  const Icon = getInterestIcon(interest);
                  return (
                    <span key={index} className="inline-flex items-center gap-1.5 bg-secondary/60 rounded-full px-3 py-1.5 text-sm">
                      <Icon className="h-3.5 w-3.5" />
                      {t(interest)}
                    </span>
                  );
                })}
              </div>
            </div>

            {/* GALLERY Section */}
            {spicyPhotos.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <span aria-hidden="true">🔥</span>
                  {t('boyfriend_profile.private_content')}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {spicyPhotos.map((photo, index) => {
                    const isLocked = photo.locked;
                    const isLockable = photo.isLockable;
                    return (
                      <div
                        key={photo.unlockKey}
                        className={`relative aspect-[3/4] rounded-lg overflow-hidden border border-border ${isLocked && isLockable && photo.priceTokens != null && photo.priceTokens > 0 ? 'cursor-pointer' : ''}`}
                        onClick={
                          isLocked
                            ? (isLockable && photo.priceTokens != null && photo.priceTokens > 0
                                ? () => handleUnlockSpicyPhoto(photo.unlockKey, photo.priceTokens)
                                : undefined)
                            : () => setFullScreenPhoto(photo.url)
                        }
                      >
                        <img
                          src={photo.url}
                          alt={photo.description || `${t('boyfriend_profile.private_photo')} ${index + 1}`}
                          className={`w-full h-full object-cover ${isLocked ? 'blur-md' : 'cursor-pointer'}`}
                        />
                        {isLocked && (
                          <div className="absolute inset-0 bg-black/30 flex flex-col items-center justify-center px-3 py-2 text-center pointer-events-none">
                            <Lock className="h-5 w-5 text-white mb-1" />
                            {photo.description && (
                              <p className="text-[10px] text-white/90 line-clamp-2 mb-1">{photo.description}</p>
                            )}
                            {isLockable && photo.priceTokens != null && photo.priceTokens > 0 && (
                              <span
                                className="inline-flex items-center gap-1 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-full px-2.5 py-1 text-xs font-semibold shadow-md"
                              >
                                <Sparkles className="h-3.5 w-3.5 shrink-0" />
                                <span>{t('boyfriend_profile.unlock')}</span>
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-3 items-center">
                  <Button
                    onClick={() => setShowGenerateModal(true)}
                    variant="outline"
                    className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 border-transparent text-white shadow-md hover:shadow-lg transition-all"
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    {t('boyfriend_profile.generate_photo')}
                  </Button>
                </div>
              </div>
            )}

            {/* ABOUT Section */}
            <div className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('boyfriend_profile.about')}</h3>
              <div className="rounded-xl border border-border bg-card/40 divide-y divide-border">
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-sm text-muted-foreground">{t('boyfriend_profile.name')}</span>
                  <span className="text-sm font-medium">{profile.name}</span>
                </div>
                {profile.location && (
                  <div className="flex items-center justify-between px-4 py-3">
                    <span className="text-sm text-muted-foreground">{t('boyfriend_profile.location')}</span>
                    <span className="text-sm font-medium inline-flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                      {profile.location}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-sm text-muted-foreground">{t('boyfriend_profile.voice')}</span>
                  <button
                    type="button"
                    onClick={handlePlayVoice}
                    disabled={isPlayingVoice}
                    aria-label={t('boyfriend_profile.my_voice')}
                    className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-md hover:shadow-lg transition-all disabled:opacity-60"
                  >
                    {isPlayingVoice ? (
                      <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                    ) : (
                      <Play className="h-3.5 w-3.5 fill-current ml-0.5" />
                    )}
                  </button>
                </div>
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-sm text-muted-foreground">{t('boyfriend_profile.messages_sent')}</span>
                  <span className="text-sm font-medium">{(profile.stats as any)?.messages ?? 0}</span>
                </div>
              </div>
            </div>

            {/* SETTINGS Section */}
            <div className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('boyfriend_profile.settings')}</h3>
              <div className="rounded-xl border border-border bg-card/40 divide-y divide-border overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowResetConfirm(true)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-secondary/40 transition-colors"
                >
                  <span className="inline-flex items-center gap-3 text-sm">
                    <RotateCcw className="h-4 w-4 text-muted-foreground" />
                    {t('boyfriend_profile.reset_conversation')}
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-destructive/10 transition-colors"
                >
                  <span className="inline-flex items-center gap-3 text-sm text-destructive">
                    <Trash2 className="h-4 w-4" />
                    {t('boyfriend_profile.delete_chat')}
                  </span>
                  <ChevronRight className="h-4 w-4 text-destructive/70" />
                </button>
              </div>
            </div>

          </div>
        </div>

      </div>
    </div>

      {/* Generate Content Modal */}
      <Dialog open={showGenerateModal} onOpenChange={setShowGenerateModal}>
        <DialogContent
          className="w-screen h-screen max-w-none m-0 p-0 rounded-none overflow-y-auto"
          onOpenAutoFocus={(e) => e.preventDefault()}
          hideCloseButton
        >
          <div className="sticky top-0 z-20 bg-background bg-gradient-to-br from-primary/20 via-secondary/10 to-accent/20 p-6 border-b border-primary/20" style={{ paddingTop: "calc(1rem + var(--safe-area-inset-top, 0px))" }}>
            <div className="absolute inset-0 bg-gradient-to-r from-pink-500/10 to-purple-500/10" />
            <div className="relative z-10 mb-3">
              <div className="flex items-center justify-center relative">
                <button
                  onClick={() => setShowGenerateModal(false)}
                  className="absolute left-0 p-2 rounded-full hover:bg-white/10 transition-colors"
                >
                  <ArrowLeft className="h-6 w-6" />
                </button>
                <DialogTitle className="px-10 text-2xl md:text-3xl font-bold bg-gradient-romantic bg-clip-text text-transparent text-center">
                  {t('boyfriend_profile.generate_custom_content')}
                </DialogTitle>
              </div>
            </div>
            <DialogDescription className="relative z-10 text-center text-muted-foreground text-base md:text-lg">
              {t('boyfriend_profile.create_personalized')} {profile.name}
            </DialogDescription>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Sample Video Preview */}
            <div className="space-y-4">
              <Card className="overflow-hidden border-2 border-primary/20">
                <div className="relative">
                  <img 
                    src={sampleCoupleHug} 
                    alt="Sample romantic couple"
                    className="w-full h-64 md:h-80 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <h4 className="font-bold text-lg mb-1">{t('boyfriend_profile.romantic_moments')}</h4>
                    <p className="text-sm opacity-90">{t('boyfriend_profile.create_intimate')} {profile.name}</p>
                  </div>
                  <div className="absolute top-4 right-4">
                    <div className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold animate-pulse">
                      {t('boyfriend_profile.sample')}
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Prompt Input */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">{t('boyfriend_profile.describe_vision')}</h3>
              <Textarea
                placeholder={t('boyfriend_profile.vision_placeholder').replace('{{name}}', profile.name || '')}
                value={generatePrompt}
                onChange={(e) => setGeneratePrompt(e.target.value)}
                rows={4}
                autoFocus={false}
                className="border-2 border-primary/20 focus:border-primary/40 resize-none placeholder:text-gray-400"
              />
            </div>

            {/* Photo Upload */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">{t('boyfriend_profile.upload_photo')}</h3>
              <div className="space-y-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      console.log('Selected file:', file);
                      // Convert file to base64 data URL
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        const base64String = reader.result as string;
                        setUploadedUserPhoto(base64String);
                        console.log('User photo uploaded and converted to base64');
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20 cursor-pointer"
                />
                {uploadedUserPhoto && (
                  <div className="relative w-32 h-32 border-2 border-primary/20 rounded-lg overflow-hidden">
                    <img 
                      src={uploadedUserPhoto} 
                      alt="Uploaded preview" 
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => setUploadedUserPhoto('')}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Footer with Actions */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-4 border-t border-primary/20">
              <div className="flex items-center gap-2 order-2 md:order-1">
                <Coins className="h-5 w-5 text-amber-500" />
                <span className="font-semibold">{modalTokens} {t('boyfriend_profile.tokens_available')}</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setShowGenerateModal(false);
                    try {
                      if (typeof sessionStorage !== 'undefined') {
                        sessionStorage.setItem('resumeGenerationAfterTokens', '1');
                      }
                    } catch {}
                    onTokensClick?.();
                  }}
                  className="ml-1 border-amber-500/50 text-amber-500 hover:bg-amber-500/10"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {t('tokens.purchase_more_cta')}
                </Button>
              </div>
              <div className="flex gap-3 order-1 md:order-2 w-full md:w-auto">
                <Button
                  onClick={handleGenerate}
                  disabled={isGenerating || !generatePrompt.trim()}
                  variant="romantic"
                  className="flex-1 md:flex-none bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 hover:shadow-lg transition-all duration-300"
                >
                  {isGenerating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {t('boyfriend_profile.generating')}
                    </>
                  ) : (
                    <>
                      <Zap className="h-4 w-4 mr-2" />
                      {t('boyfriend_profile.generate')} ({generationType === 'photo' ? 20 : 50} {t('boyfriend_profile.tokens')})
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Token purchase flow removed here; handled by global TokenBalanceModal via onTokensClick */}

      {/* Generation Progress Dialog */}
      <Dialog open={showGenerationDialog} onOpenChange={setShowGenerationDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-center text-2xl">
              {isGenerating ? t('boyfriend_profile.generating_image') : t('boyfriend_profile.image_generated')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-6">
            {isGenerating ? (
              <div className="flex flex-col items-center justify-center space-y-6">
                <div className="w-full max-w-md space-y-3">
                  <Progress value={undefined} className="h-2" />
                  <p className="text-center text-muted-foreground">
                    {t('boyfriend_profile.creating_custom')}
                  </p>
                </div>
                <div className="animate-pulse text-sm text-muted-foreground">
                  {t('boyfriend_profile.take_moments')}
                </div>
              </div>
            ) : generatedImageUrl ? (
              <div className="space-y-4">
                <div className="relative w-full aspect-[3/4] overflow-hidden rounded-lg bg-muted">
                  <img 
                    src={generatedImageUrl} 
                    alt="Generated image" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="flex gap-3 justify-center">
                  {generatedImageId && (
                    <Button
                      variant="outline"
                      onClick={() => setReportImageId(generatedImageId)}
                      className="h-10"
                    >
                      <Flag className="h-4 w-4 mr-2" />
                      {t('image.report')}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowGenerationDialog(false);
                      setGeneratedImageUrl(null);
                    }}
                    className="h-10"
                  >
                    {t('boyfriend_profile.close')}
                  </Button>
                  <Button
                    onClick={() => {
                      // Download the image
                      if (generatedImageUrl) {
                        const link = document.createElement('a');
                        link.href = generatedImageUrl;
                        link.download = `generated-${profile?.name || 'image'}-${Date.now()}.png`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }
                    }}
                    className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 h-10"
                  >
                    {t('boyfriend_profile.save_image')}
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      <ReportImageDialog
        imageId={reportImageId}
        onClose={() => setReportImageId(null)}
        onReported={() => {
          setShowGenerationDialog(false);
          setGeneratedImageUrl(null);
          setGeneratedImageId(null);
        }}
      />

      {/* Delete Chat Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('boyfriend_profile.delete_chat')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('boyfriend_profile.delete_chat_confirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingChat}>{t('boyfriend_profile.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDeleteChat(); }}
              disabled={isDeletingChat}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('boyfriend_profile.delete_chat')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Conversation Confirmation */}
      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('boyfriend_profile.reset_conversation')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('boyfriend_profile.reset_conversation_confirm', { name: profile.name || '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isResettingChat}>{t('boyfriend_profile.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleResetConversation(); }}
              disabled={isResettingChat}
            >
              {t('boyfriend_profile.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Full Screen Photo Modal */}
      {fullScreenPhoto && (
        <div 
          className="fixed inset-0 bg-black/90 z-[9999] flex items-center justify-center p-4"
          onClick={(e) => {
            console.log('Full screen background clicked');
            e.stopPropagation();
            setFullScreenPhoto(null);
          }}
        >
          <div className="relative max-w-full max-h-full">
            <button
              type="button"
              onClick={(e) => {
                console.log('X button clicked');
                e.preventDefault();
                e.stopPropagation();
                setFullScreenPhoto(null);
              }}
              className="absolute top-4 right-4 bg-black/70 text-white rounded-full p-3 hover:bg-black/90 transition-colors z-[99999] border-2 border-white/20 hover:border-white/40 cursor-pointer"
              style={{ pointerEvents: 'auto' }}
            >
              <X className="h-6 w-6 pointer-events-none" />
            </button>
            <img
              src={fullScreenPhoto}
              alt="Full screen private photo"
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};